/**
 * RiskProtection Service
 * API integration for risk-protection (EPI/EPC) link management
 *
 * @version 2025.1.0
 */
import apiClient from './apiClient';

export interface RiskProtectionEpi {
  id: number;
  nome: string;
  tipoDeRisco: string;
  risco: string;
  metodo: string;
  atenuacao?: string | null;
  eSocial?: string;
  ativo: string;
}

export interface RiskProtectionEpc {
  id: number;
  nome: string;
  tipoDeRisco: string;
  risco: string;
  revisao?: string;
  eSocial?: string;
}

export interface RiskProtection {
  id: number;
  occupationalRiskId: number;
  epiId?: number | null;
  epcId?: number | null;
  isEffective: boolean;
  notes?: string | null;
  epi?: RiskProtectionEpi | null;
  epc?: RiskProtectionEpc | null;
  createdAt: string;
  updatedAt: string;
}

export interface RiskProtectionListResponse {
  protections: RiskProtection[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface RiskProtectionListParams {
  type?: 'EPI' | 'EPC';
  isEffective?: boolean;
  page?: number;
  limit?: number;
}

export interface CreateRiskProtectionData {
  epiId?: number | null;
  epcId?: number | null;
  isEffective?: boolean;
  notes?: string;
}

export interface UpdateRiskProtectionData {
  isEffective?: boolean;
  notes?: string;
}

class RiskProtectionService {
  private getBaseUrl(riskId: number | string): string {
    return `/occupational-risks/${riskId}/protections`;
  }

  /**
   * List protections for a risk
   */
  async getProtections(
    riskId: number | string,
    params?: RiskProtectionListParams
  ): Promise<RiskProtectionListResponse> {
    const response = await apiClient.get(this.getBaseUrl(riskId), { params });
    return response.data.data || response.data;
  }

  /**
   * Link an EPI/EPC to a risk
   */
  async createProtection(
    riskId: number | string,
    data: CreateRiskProtectionData
  ): Promise<RiskProtection> {
    const response = await apiClient.post(this.getBaseUrl(riskId), data);
    return response.data.data || response.data;
  }

  /**
   * Update a protection (effectiveness/notes)
   */
  async updateProtection(
    riskId: number | string,
    protectionId: number | string,
    data: UpdateRiskProtectionData
  ): Promise<RiskProtection> {
    const response = await apiClient.put(
      `${this.getBaseUrl(riskId)}/${protectionId}`,
      data
    );
    return response.data.data || response.data;
  }

  /**
   * Unlink a protection (soft delete)
   */
  async deleteProtection(
    riskId: number | string,
    protectionId: number | string
  ): Promise<void> {
    await apiClient.delete(`${this.getBaseUrl(riskId)}/${protectionId}`);
  }
}

export const riskProtectionService = new RiskProtectionService();
export default riskProtectionService;
